import {
  type FormulaReferenceValue,
  type FormulaRuntimeColumn,
  getSkopSupportedRefs,
  matchIncrementTemplate,
  parseFormulaConfig,
  readSkopFormulaFieldValue,
  recomputeFormulaFields,
  resolveSkopFormulaConfig,
} from '@skop-app/lib-formula-core'
import { getExtendedTokens } from '@skop-app/lib-formula-core/preview'

import type { FormulaEvaluationContext, SupportedKeys } from '@/types'
import {
  computeMassFormulaEntries,
  withMassFormulaValues,
} from '@/domain/product/mass/massFormula'
import { parseMassFormulaSettings } from '@/domain/product/mass/massFormula.config'

import {
  formulaOverride,
  generateIncrement,
  getFormulaValue,
  shouldFreezeFormulaEvaluation,
} from './formula.helpers'

type Row = Record<string, unknown>
export type ManageFormulaColumn = { field: string; config: unknown }

/** getFormulaValue is the existing lookup adapter; the core receives literals. */
export const resolveManageFormulaReference = (
  referenceName: string,
  row: Row,
  context: FormulaEvaluationContext,
): FormulaReferenceValue => {
  const expression = getFormulaValue(referenceName, row, context)
  if (expression === undefined) {
    const root = referenceName.split('.')[0]
    const raw = readSkopFormulaFieldValue(row, root)
    if (
      raw &&
      /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(raw) &&
      !context.optionsByID?.[root]
    ) {
      return { status: 'pending', referenceName }
    }
    return { status: 'missing-inputs', referenceName }
  }
  const value = /^"[\s\S]*"$/.test(expression)
    ? expression.slice(1, -1)
    : expression
  if (
    /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(value) &&
    !context.optionsByID?.[referenceName.split('.')[0]] &&
    !referenceName.endsWith('.id')
  )
    return { status: 'pending', referenceName }
  if (value === '-' || value === '—' || value === '')
    return { status: 'missing-inputs', referenceName }
  if (['NaN', 'Infinity', '-Infinity'].includes(value))
    return { status: 'invalid', referenceName }
  return value
}

export const recomputeManageFormulaFields = (params: {
  row: Row
  columns: ManageFormulaColumn[]
  context: FormulaEvaluationContext
  supportedKeys?: SupportedKeys[]
  allRowData?: Row[]
  getIncrementRows?: () => Row[]
  force?: boolean
  incrementMode?: 'generate' | 'preserve'
  preserveSuppliedValues?: boolean
  allocateIncrements?: boolean
  targetFields?: ReadonlySet<string>
}) => {
  const { row, context, columns, force = false } = params
  const massEntries = computeMassFormulaEntries({ row, columns, context })
  const calculationRow = withMassFormulaValues(row, massEntries)
  const supportedRefs = new Set([
    ...getSkopSupportedRefs(
      context.supportedKeys?.length
        ? context.supportedKeys
        : (params.supportedKeys ?? []),
    ),
    ...Object.keys(row),
    ...(Array.isArray(row.properties)
      ? row.properties.flatMap((property: unknown) =>
          property &&
          typeof property === 'object' &&
          'key' in property &&
          typeof property.key === 'string'
            ? [property.key]
            : [],
        )
      : []),
    ...Object.keys(context.optionsByID ?? {}),
    ...columns.map(({ field }) => field),
  ])
  // Dotted source references are real property metadata, never editor samples.
  columns.forEach(({ config }) => {
    parseFormulaConfig(config)?.fields.forEach((field) => {
      if (
        field.referenceName.includes('.') ||
        field.formula.trim() === field.referenceName ||
        field.formula.trim() === ''
      )
        supportedRefs.add(field.referenceName)
    })
  })
  const fields = columns.flatMap(
    ({ field, config }): FormulaRuntimeColumn[] => {
      if (parseMassFormulaSettings(config)) return []
      const normalized = resolveSkopFormulaConfig({
        value: config,
        fieldKey: field,
        supportedRefs: [...supportedRefs],
      })
      return normalized
        ? [
            {
              field,
              config: {
                ...normalized.runtimeConfig,
                formulaField: {
                  ...normalized.runtimeConfig.formulaField,
                  referenceName: field,
                },
              },
            },
          ]
        : []
    },
  )
  const required = new Set(
    params.targetFields ?? columns.map(({ field }) => field),
  )
  const entries = getExtendedTokens(
    Object.fromEntries(
      fields.flatMap(({ config }) =>
        [...config.fields, config.formulaField].map((entry) => [
          entry.referenceName,
          entry.formula,
        ]),
      ),
    ),
  )
  for (const field of [...required]) {
    entries[field.toLowerCase()]?.dependencies.forEach((reference) => {
      const dependency = columns.find(
        ({ field }) => field.toLowerCase() === reference,
      )
      if (dependency) required.add(dependency.field)
    })
  }
  const rows = [...(params.allRowData ?? [])]
  let incrementRowsLoaded = false
  const outcome = recomputeFormulaFields({
    fields: fields.filter(({ field }) => required.has(field)),
    rowContext: calculationRow,
    mode: force ? 'duplicate' : 'edit-existing',
    resolver: {
      supportedRefs: [...supportedRefs],
      resolve: (referenceName) => {
        const massEntry = massEntries.find(
          ({ field }) => field.toLowerCase() === referenceName.toLowerCase(),
        )
        return massEntry
          ? massEntry.state
          : resolveManageFormulaReference(
              referenceName,
              calculationRow,
              context,
            )
      },
      readFieldValue: (field) =>
        readSkopFormulaFieldValue(calculationRow, field),
    },
    preserveExisting: (field, rawValue) => {
      if (
        params.preserveSuppliedValues &&
        rawValue &&
        rawValue !== '-' &&
        rawValue !== '—'
      )
        return true
      return (
        !force &&
        shouldFreezeFormulaEvaluation({
          rawValue,
          formula: field.config.formulaField.formula,
        })
      )
    },
    allocation:
      params.allocateIncrements === false
        ? undefined
        : {
            allocate: ({ field, template }) => {
              if (!incrementRowsLoaded) {
                rows.push(...(params.getIncrementRows?.() ?? []))
                incrementRowsLoaded = true
              }
              const current =
                formulaOverride(readSkopFormulaFieldValue(row, field)) ??
                readSkopFormulaFieldValue(row, field)
              const suffix =
                params.incrementMode === 'preserve'
                  ? (matchIncrementTemplate(template, current)?.rawNumber ??
                    /(\d{3,})$/.exec(current ?? '')?.[1])
                  : undefined
              const prefix = template.replace('{INC}', '').replace(/-$/, '')
              const value = template.replace(
                '{INC}',
                suffix ?? generateIncrement(prefix, field, rows),
              )
              rows.push({ properties: [{ key: field, values: [value] }] })
              return value
            },
            reserve: () => undefined,
            isAllocatedValue: () => false,
          },
  })
  for (const { field, state, preserveManual } of massEntries) {
    if (!required.has(field)) continue
    outcome.fieldStates[field] = state
    if (state.status === 'valid') {
      if (!preserveManual) outcome.computedFields[field] = String(state.value)
    } else {
      outcome.skippedFields[field] = preserveManual
        ? 'manual-override'
        : state.status
    }
  }
  return outcome
}

/** Clear missing/invalid results, preserve pending values; '-' is display only. */
export const getManageFormulaChanges = (
  outcome: ReturnType<typeof recomputeManageFormulaFields>,
): Record<string, string> => ({
  ...outcome.computedFields,
  ...Object.fromEntries(
    Object.entries(outcome.fieldStates).flatMap(([field, state]) =>
      (state.status === 'missing-inputs' || state.status === 'invalid') &&
      outcome.skippedFields[field] !== 'manual-override'
        ? [[field, '']]
        : [],
    ),
  ),
})
